"use client";
import { SchemaMarkup } from '../components/SEO/SchemaMarkup';
import { ENTITY } from '../data/entityData';
import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Award, Users, Shield } from 'lucide-react';
import PageWrapper from '../components/PageWrapper';
import GeometricPattern from '../components/GeometricPattern';
import CrescentMoon from '../components/CrescentMoon';
import { COMPANY, STATS } from '../data/mock';
import { brandConfig } from '../brandConfig';
import { sacredRise, imageReveal, stagger } from '../lib/animations';

const values = [
  { icon: Heart, title: 'Spirituality', arabic: 'روحانية', desc: 'Every detail honors the sacredness of your journey.' },
  { icon: Shield, title: 'Trust', arabic: 'ثقة', desc: 'Built over 15+ years serving Ethiopian Muslims.' },
  { icon: Users, title: 'Comfort', arabic: 'راحة', desc: 'So you can focus only on your Ibadah.' },
  { icon: Award, title: 'Responsibility', arabic: 'مسؤولية', desc: 'Your journey is our personal commitment.' },
];

export default function About() {
  return (
    <PageWrapper>
      <SchemaMarkup
        pageTitle={`About ${brandConfig.companyName} | Sacred Travel Experience`}
        pageDescription={`Learn about ${brandConfig.companyName}, Ethiopia's trusted Umrah and Hajj agency since 2011. 100% visa success rate and 24/7 dedicated support.`}
        pageUrl="/about"
      />
      <section style={{ position: 'relative', minHeight: '55vh', overflow: 'hidden', background: '#0A1628', display: 'flex', alignItems: 'center' }}>
        <img src="https://images.unsplash.com/photo-1564769625905-50e93615e769?w=1920&h=600&fit=crop&q=80" alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,22,40,0.85)' }} />
        <GeometricPattern color="#C9A84C" opacity={0.05} size={100} />
        <div style={{ position: 'relative', zIndex: 2, maxWidth: 'var(--container)', margin: '0 auto', padding: '120px var(--gutter) 60px' }}>
          <div className="arabic-text" style={{ fontSize: 18, color: '#C9A84C' }}>{COMPANY.bismillah}</div>
          <h1 style={{ fontFamily: 'Amiri', fontWeight: 700, color: '#FFFFFF', fontSize: 'var(--h1)', marginTop: 16, lineHeight: 1.1 }}>
            About <span style={{ color: '#C9A84C' }}>{brandConfig.companyName}.</span>
          </h1>
          <div className="arabic-text" style={{ fontStyle: 'italic', color: 'rgba(251,248,240,0.85)', fontSize: 22, marginTop: 12 }}>
            رحلة إيمان، عمر من البركات
          </div>
          <p style={{ fontFamily: 'Inter', color: '#FBF8F0', fontSize: 'var(--body-lg)', maxWidth: 560, marginTop: 18 }}>
            A Journey of Faith. A Lifetime of Blessings.
          </p>
        </div>
      </section>

      {/* STORY */}
      <section style={{ background: '#FBF8F0', padding: 'var(--section) 0' }}>
        <div className="about-grid" style={{
          maxWidth: 'var(--container)', margin: '0 auto', padding: '0 var(--gutter)',
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'center',
        }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={imageReveal} style={{ position: 'relative' }}>
            <img src="https://images.unsplash.com/photo-1592326871020-04f58c1a52f3?w=800&h=900&fit=crop&q=80" alt="Mecca" style={{ width: '100%', height: 500, objectFit: 'cover' }} />
            <img src="https://images.unsplash.com/photo-1591604129939-f1efa4d9f7fa?w=400&h=400&fit=crop&q=80" alt="Medina" style={{
              position: 'absolute', bottom: -40, right: -40, width: 240, height: 240,
              objectFit: 'cover', border: '8px solid #FBF8F0',
            }} className="overlap-img" />
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={sacredRise}>
            <div style={{ fontFamily: 'Inter', fontWeight: 500, color: '#1B6B3A', fontSize: 11, letterSpacing: '0.3em', textTransform: 'uppercase' }}>
              — Our Story
            </div>
            <h2 style={{ fontFamily: 'Amiri', fontWeight: 700, color: '#0A1628', fontSize: 'var(--h2)', marginTop: 12, lineHeight: 1.15 }}>
              Serving the sacred journey<br />of Ethiopian Muslims.
            </h2>
            <p style={{ fontFamily: 'Inter', color: '#0A1628', fontSize: 'var(--body-lg)', lineHeight: 1.85, marginTop: 20 }}>
              For over 15 years, {brandConfig.companyName} has been the trusted partner for thousands of Ethiopian Muslims undertaking the sacred journey of Umrah. Born from a deep love for our deen and our community, we exist to make the path to the Holy Land easier, safer, and more blessed.
            </p>
            <p style={{ fontFamily: 'Inter', color: '#0A1628', fontSize: 'var(--body-lg)', lineHeight: 1.85, marginTop: 16 }}>
              Every package we arrange, every visa we secure, every hotel we book — it's done with the understanding that we're not just selling travel, Alhamdulillah, we're honoring a once-in-a-lifetime spiritual responsibility.
            </p>
            <div style={{ marginTop: 28, padding: 20, background: '#FFFFFF', borderLeft: '4px solid #C9A84C' }}>
              <p style={{ fontFamily: 'Amiri', fontStyle: 'italic', color: '#0A1628', fontSize: 17, lineHeight: 1.7, margin: 0 }}>
                “Let us take care of the arrangements, so you can focus on your Ibadah.”
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* VALUES */}
      <section style={{ background: '#1B6B3A', padding: 'var(--section) 0', position: 'relative', overflow: 'hidden' }}>
        <GeometricPattern color="#FBF8F0" opacity={0.05} size={100} />
        <div style={{ position: 'relative', zIndex: 2, maxWidth: 'var(--container)', margin: '0 auto', padding: '0 var(--gutter)' }}>
          <div style={{ textAlign: 'center', marginBottom: 60 }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <CrescentMoon size={40} color="#FBF8F0" />
            </div>
            <div style={{ fontFamily: 'Inter', fontWeight: 500, color: '#FBF8F0', fontSize: 11, letterSpacing: '0.3em', textTransform: 'uppercase', marginTop: 12, opacity: 0.85 }}>
              — Our Commitment —
            </div>
            <h2 style={{ fontFamily: 'Amiri', fontWeight: 700, color: '#FBF8F0', fontSize: 'var(--h2)', marginTop: 12 }}>
              {COMPANY.tagline}
            </h2>
            <div className="arabic-text" style={{ fontStyle: 'italic', color: 'rgba(251,248,240,0.85)', fontSize: 20, marginTop: 8 }}>
              {COMPANY.taglineSub}
            </div>
          </div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger(0.1)}
            className="values-grid"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 24 }}>
            {values.map((v) => {
              const Icon = v.icon;
              return (
                <motion.div key={v.title} variants={sacredRise} style={{
                  background: 'rgba(10,22,40,0.4)', backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(201,168,76,0.3)', padding: 32, textAlign: 'center',
                }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: '50%', background: '#C9A84C',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto',
                  }}>
                    <Icon size={24} color="#1B6B3A" strokeWidth={2} />
                  </div>
                  <div style={{ fontFamily: 'Amiri', fontWeight: 700, color: '#FBF8F0', fontSize: 22, marginTop: 18 }}>
                    {v.title}
                  </div>
                  <div className="arabic-text" style={{ fontStyle: 'italic', color: '#C9A84C', fontSize: 14, marginTop: 4 }}>
                    {v.arabic}
                  </div>
                  <p style={{ fontFamily: 'Inter', color: '#FBF8F0', fontSize: 13, lineHeight: 1.7, marginTop: 12 }}>
                    {v.desc}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <style>{`
        @media (max-width: 900px) {
          .about-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
          .overlap-img { display: none !important; }
          .values-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 540px) {
          .values-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </PageWrapper>
  );
}
